// Type badge colors - using semantic naming for theming
export const FIELD_TYPE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  string: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-500/20' },
  number: { bg: 'bg-green-500/10', text: 'text-green-600 dark:text-green-400', border: 'border-green-500/20' },
  integer: { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-500/20' },
  boolean: { bg: 'bg-purple-500/10', text: 'text-purple-600 dark:text-purple-400', border: 'border-purple-500/20' },
  array: { bg: 'bg-orange-500/10', text: 'text-orange-600 dark:text-orange-400', border: 'border-orange-500/20' },
  object: { bg: 'bg-cyan-500/10', text: 'text-cyan-600 dark:text-cyan-400', border: 'border-cyan-500/20' },
  enum: { bg: 'bg-pink-500/10', text: 'text-pink-600 dark:text-pink-400', border: 'border-pink-500/20' },
  null: { bg: 'bg-gray-500/10', text: 'text-gray-600 dark:text-gray-400', border: 'border-gray-500/20' },
};

// Format badge colors
export const FORMAT_BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  date: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
  'date-time': { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400' },
  email: { bg: 'bg-indigo-500/10', text: 'text-indigo-600 dark:text-indigo-400' },
  uuid: { bg: 'bg-violet-500/10', text: 'text-violet-600 dark:text-violet-400' },
  uri: { bg: 'bg-teal-500/10', text: 'text-teal-600 dark:text-teal-400' },
  phone: { bg: 'bg-rose-500/10', text: 'text-rose-600 dark:text-rose-400' },
  'us-state': { bg: 'bg-sky-500/10', text: 'text-sky-600 dark:text-sky-400' },
};

export interface ParsedFieldType {
  baseType: string;
  format?: string;
  isArray?: boolean;
  arrayItemType?: string;
}

/**
 * Parse field type string like "string (uuid)" or "array<object>"
 * Returns the base type and optional format
 */
export function parseFieldType(typeString: string): ParsedFieldType {
  if (!typeString) return { baseType: 'string' };

  // Handle array notation: array<type> or type[]
  const arrayMatch = typeString.match(/^array<(.+)>$/i) || typeString.match(/^(.+)\[\]$/);
  if (arrayMatch) {
    return {
      baseType: 'array',
      isArray: true,
      arrayItemType: arrayMatch[1].trim(),
    };
  }

  // Handle format in parentheses: string (uuid)
  const formatMatch = typeString.match(/^(\w+)\s*\(([^)]+)\)$/);
  if (formatMatch) {
    return {
      baseType: formatMatch[1].toLowerCase(),
      format: formatMatch[2].trim(),
    };
  }

  // Handle enum notation
  if (typeString.toLowerCase() === 'enum' || typeString.includes('|')) {
    return { baseType: 'enum' };
  }

  return { baseType: typeString.toLowerCase() };
}

export interface NestedFieldInfo {
  name: string;
  type: string;
  description?: string;
  required?: boolean;
  format?: string;
  enumValues?: string[];
  constraints?: {
    minLength?: number;
    maxLength?: number;
    minimum?: number;
    maximum?: number;
    pattern?: string;
  };
  nestedFields?: NestedFieldInfo[];
}

/**
 * Extract nested fields from a JSON Schema property
 */
export function extractNestedFields(
  schema: object,
  propertyName: string
): NestedFieldInfo[] | undefined {
  const schemaAny = schema as Record<string, unknown>;
  const properties = schemaAny.properties as Record<string, unknown> | undefined;

  if (!properties || !properties[propertyName]) return undefined;

  const propSchema = properties[propertyName] as Record<string, unknown>;

  // Handle object type with nested properties
  if (propSchema.type === 'object' && propSchema.properties) {
    return extractPropertiesAsFields(
      propSchema.properties as Record<string, unknown>,
      (propSchema.required as string[]) || []
    );
  }

  // Handle array of objects
  if (propSchema.type === 'array' && propSchema.items) {
    const items = propSchema.items as Record<string, unknown>;
    if (items.type === 'object' && items.properties) {
      return extractPropertiesAsFields(
        items.properties as Record<string, unknown>,
        (items.required as string[]) || []
      );
    }
  }

  return undefined;
}

/**
 * Convert JSON Schema properties object to NestedFieldInfo array
 */
function extractPropertiesAsFields(
  properties: Record<string, unknown>,
  required: string[]
): NestedFieldInfo[] {
  return Object.entries(properties).map(([name, prop]) => {
    const propObj = prop as Record<string, unknown>;
    const type = getSchemaType(propObj);

    const field: NestedFieldInfo = {
      name,
      type,
      description: propObj.description as string | undefined,
      required: required.includes(name),
      format: propObj.format as string | undefined,
      enumValues: propObj.enum as string[] | undefined,
      constraints: {
        minLength: propObj.minLength as number | undefined,
        maxLength: propObj.maxLength as number | undefined,
        minimum: propObj.minimum as number | undefined,
        maximum: propObj.maximum as number | undefined,
        pattern: propObj.pattern as string | undefined,
      },
    };

    // Recursively extract nested object fields
    if (propObj.type === 'object' && propObj.properties) {
      field.nestedFields = extractPropertiesAsFields(
        propObj.properties as Record<string, unknown>,
        (propObj.required as string[]) || []
      );
    }

    // Handle array items
    if (propObj.type === 'array' && propObj.items) {
      const items = propObj.items as Record<string, unknown>;
      if (items.type === 'object' && items.properties) {
        field.nestedFields = extractPropertiesAsFields(
          items.properties as Record<string, unknown>,
          (items.required as string[]) || []
        );
      }
    }

    return field;
  });
}

/**
 * Get human-readable type from JSON Schema property
 */
function getSchemaType(prop: Record<string, unknown>): string {
  if (prop.enum) return 'enum';

  const type = prop.type as string | string[] | undefined;

  if (Array.isArray(type)) {
    // Handle nullable types like ["string", "null"]
    const nonNullType = type.find(t => t !== 'null');
    return nonNullType || 'string';
  }

  if (type === 'array' && prop.items) {
    const items = prop.items as Record<string, unknown>;
    const itemType = getSchemaType(items);
    return `array<${itemType}>`;
  }

  if (type && prop.format) {
    return `${type} (${prop.format})`;
  }

  return type || 'string';
}

/**
 * Get type color configuration, with fallback to default
 */
export function getTypeColors(type: string) {
  const parsed = parseFieldType(type);
  return FIELD_TYPE_COLORS[parsed.baseType] || FIELD_TYPE_COLORS.string;
}

/**
 * Get format color configuration, with fallback
 */
export function getFormatColors(format: string) {
  return FORMAT_BADGE_COLORS[format] || FORMAT_BADGE_COLORS.date;
}
